import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Button } from "../../../ui/button";
import { Textarea } from "../../../ui/textarea";
import { Checkbox } from "../../../ui/checkbox";
import { Badge } from "../../../ui/badge";
import { Label } from "../../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Sparkles,
  Lightbulb,
  CheckSquare,
  Loader2,
  Plus,
  Edit2,
  Trash2,
  Calendar,
  Flag,
  ArrowRight,
} from "lucide-react";
import { UserTakeaway, CreateTakeawayRequest } from "../../../../api/userTakeaways";
import { UserCategory } from "../../../../api/userCategories";
import { cn } from "../../../../lib/utils";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { convertInsightToActions } from "../../../../api/insightAI";
import { GeneratingLoader } from "../../../ui/generating-loader";

interface SuggestedAction {
  id: string;
  content: string;
  priority: "high" | "medium" | "low";
  suggestedDueDate?: number;
  selected: boolean;
  category?: string;
}

interface InsightToActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insight: UserTakeaway | null;
  categories: UserCategory[];
  onCreateActions: (actions: Omit<CreateTakeawayRequest, "type" | "source">[]) => Promise<void>;
}

const InsightToActionDialog: React.FC<InsightToActionDialogProps> = ({
  open,
  onOpenChange,
  insight,
  categories,
  onCreateActions,
}) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const [isGenerating, setIsGenerating] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState<SuggestedAction[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [customAction, setCustomAction] = useState("");

  // Generate actions when dialog opens with an insight
  useEffect(() => {
    if (open && insight) {
      generateActions();
    }
  }, [open, insight]);

  const generateActions = async () => {
    if (!insight) return;

    setIsGenerating(true);
    setSuggestedActions([]);

    try {
      // Try API first
      const response = await convertInsightToActions(retryFetch, {
        insightId: insight._id,
      });

      if (response.status === "SUCCESS" && response.actions?.length > 0) {
        const mapped: SuggestedAction[] = response.actions.map((action, index) => ({
          id: `action-${index}-${Date.now()}`,
          content: action.content,
          priority: action.priority,
          suggestedDueDate: action.suggestedDueDate,
          selected: index === 0, // Select first by default
          category: insight.category?._id,
        }));
        setSuggestedActions(mapped);
      } else {
        // Fallback to local generation
        const localActions = generateActionsLocally(insight.content);
        setSuggestedActions(localActions);
      }
    } catch (err) {
      console.error("AI action generation failed, using local fallback:", err);
      const localActions = generateActionsLocally(insight.content);
      setSuggestedActions(localActions);
    } finally {
      setIsGenerating(false);
    }
  };

  // Local action generation fallback
  const generateActionsLocally = (content: string): SuggestedAction[] => {
    const actions: SuggestedAction[] = [];
    const contentLower = content.toLowerCase();

    // Extract action-oriented phrases
    const actionPatterns = [
      { pattern: /need to ([^.!?]+)/gi, priority: "high" as const },
      { pattern: /should ([^.!?]+)/gi, priority: "medium" as const },
      { pattern: /will ([^.!?]+)/gi, priority: "medium" as const },
      { pattern: /going to ([^.!?]+)/gi, priority: "medium" as const },
      { pattern: /try to ([^.!?]+)/gi, priority: "low" as const },
      { pattern: /consider ([^.!?]+)/gi, priority: "low" as const },
      { pattern: /practice ([^.!?]+)/gi, priority: "medium" as const },
      { pattern: /implement ([^.!?]+)/gi, priority: "high" as const },
      { pattern: /start ([^.!?]+)/gi, priority: "medium" as const },
    ];

    actionPatterns.forEach(({ pattern, priority }) => {
      let match;
      while ((match = pattern.exec(content)) !== null) {
        const actionText = match[1].trim();
        if (actionText.length > 10 && actionText.length < 200) {
          actions.push({
            id: `action-${actions.length}-${Date.now()}`,
            content: capitalizeFirst(actionText),
            priority,
            selected: actions.length === 0,
          });
        }
      }
    });

    // If no actions found, generate generic ones based on keywords
    if (actions.length === 0) {
      // Generic action suggestions based on content themes
      if (contentLower.includes("listen") || contentLower.includes("communication")) {
        actions.push({
          id: `action-0-${Date.now()}`,
          content: t("practice_active_listening") || "Practice active listening in my next meeting",
          priority: "medium",
          selected: true,
        });
      }
      if (contentLower.includes("feedback")) {
        actions.push({
          id: `action-1-${Date.now()}`,
          content: t("schedule_feedback_session") || "Schedule a feedback session this week",
          priority: "medium",
          selected: false,
        });
      }
      if (contentLower.includes("delegate") || contentLower.includes("trust")) {
        actions.push({
          id: `action-2-${Date.now()}`,
          content: t("identify_delegation_opportunity") || "Identify one task to delegate this week",
          priority: "medium",
          selected: false,
        });
      }
      if (contentLower.includes("learn") || contentLower.includes("read")) {
        actions.push({
          id: `action-3-${Date.now()}`,
          content: t("set_learning_time") || "Set aside 30 minutes for learning",
          priority: "low",
          selected: false,
        });
      }

      // If still no actions, create a generic one
      if (actions.length === 0) {
        actions.push({
          id: `action-0-${Date.now()}`,
          content: t("reflect_on_insight") || "Reflect on this insight and identify one action to take",
          priority: "low",
          selected: true,
        });
      }
    }

    // Limit to 4 actions
    return actions.slice(0, 4);
  };

  const capitalizeFirst = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const toggleActionSelection = (id: string) => {
    setSuggestedActions((prev) =>
      prev.map((action) =>
        action.id === id ? { ...action, selected: !action.selected } : action
      )
    );
  };

  const updateActionPriority = (id: string, priority: "high" | "medium" | "low") => {
    setSuggestedActions((prev) =>
      prev.map((action) =>
        action.id === id ? { ...action, priority } : action
      )
    );
  };

  const updateActionCategory = (id: string, categoryId: string) => {
    setSuggestedActions((prev) =>
      prev.map((action) =>
        action.id === id
          ? { ...action, category: categoryId === "none" ? undefined : categoryId }
          : action
      )
    );
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditText(suggestedActions[index].content);
  };

  const saveEdit = () => {
    if (editingIndex === null) return;
    setSuggestedActions((prev) =>
      prev.map((action, idx) =>
        idx === editingIndex ? { ...action, content: editText } : action
      )
    );
    setEditingIndex(null);
    setEditText("");
  };

  const removeAction = (id: string) => {
    setSuggestedActions((prev) => prev.filter((action) => action.id !== id));
  };

  const addCustomAction = () => {
    if (!customAction.trim()) return;

    setSuggestedActions((prev) => [
      ...prev,
      {
        id: `custom-${Date.now()}`,
        content: customAction.trim(),
        priority: "medium",
        selected: true,
        category: insight?.category?._id,
      },
    ]);
    setCustomAction("");
  };

  const handleCreateActions = async () => {
    const selectedActions = suggestedActions.filter((a) => a.selected);
    if (selectedActions.length === 0) return;

    setIsCreating(true);

    try {
      const actionsToCreate = selectedActions.map((action) => ({
        content: action.content,
        priority: action.priority,
        category: action.category,
        dueDate: action.suggestedDueDate,
      }));

      await onCreateActions(actionsToCreate);
      handleClose();
    } catch (error) {
      console.error("Failed to create actions:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setSuggestedActions([]);
    setCustomAction("");
    setEditingIndex(null);
    setIsGenerating(false);
    onOpenChange(false);
  };

  const selectedCount = suggestedActions.filter((a) => a.selected).length;

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "text-red-600 bg-red-50 border-red-200";
      case "medium":
        return "text-amber-600 bg-amber-50 border-amber-200";
      case "low":
        return "text-primary bg-primary/5 border-primary/20";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  if (!insight) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/8 to-indigo-100 flex items-center justify-center">
              <CheckSquare className="h-4 w-4 text-primary" />
            </div>
            {t("convert_to_actions") || "Convert to Actions"}
          </DialogTitle>
          <DialogDescription>
            {t("convert_to_actions_description") ||
              "Turn this insight into actionable items you can track and complete."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {/* Source Insight */}
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
            <div className="flex items-start gap-2">
              <Lightbulb className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-amber-600 font-medium mb-1">
                  {t("source_insight") || "Source Insight"}
                </p>
                <p className="text-sm text-gray-700 line-clamp-2">{insight.content}</p>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-2 text-muted-foreground">
              <ArrowRight className="h-4 w-4" />
              <span className="text-xs">
                {t("generates") || "generates"}
              </span>
            </div>
          </div>

          {/* Generating State */}
          {isGenerating && (
            <div className="flex items-center justify-center py-8">
              <GeneratingLoader context="extraction" />
            </div>
          )}

          {/* Suggested Actions */}
          {!isGenerating && suggestedActions.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium">
                {t("suggested_actions") || "Suggested Actions"}
              </p>

              <div className="space-y-2">
                {suggestedActions.map((action, index) => (
                  <div
                    key={action.id}
                    className={cn(
                      "p-3 rounded-lg border transition-all",
                      action.selected
                        ? "bg-primary/5/50 border-primary/20"
                        : "bg-gray-50/50 border-gray-200 opacity-60"
                    )}
                  >
                    {editingIndex === index ? (
                      <div className="space-y-2">
                        <Textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="min-h-[60px] text-sm"
                          autoFocus
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingIndex(null)}
                          >
                            {t("cancel")}
                          </Button>
                          <Button size="sm" onClick={saveEdit}>
                            {t("save")}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={action.selected}
                          onCheckedChange={() => toggleActionSelection(action.id)}
                          className="mt-0.5 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2">
                            <CheckSquare className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {action.content}
                            </p>
                          </div>

                          {/* Priority & Category */}
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <Select
                              value={action.priority}
                              onValueChange={(val) =>
                                updateActionPriority(action.id, val as "high" | "medium" | "low")
                              }
                            >
                              <SelectTrigger className="h-7 w-[100px] text-xs">
                                <Flag className="h-3 w-3 mr-1" />
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="high">
                                  <span className="text-red-600">{t("high") || "High"}</span>
                                </SelectItem>
                                <SelectItem value="medium">
                                  <span className="text-amber-600">{t("medium") || "Medium"}</span>
                                </SelectItem>
                                <SelectItem value="low">
                                  <span className="text-primary">{t("low") || "Low"}</span>
                                </SelectItem>
                              </SelectContent>
                            </Select>

                            <Select
                              value={action.category || "none"}
                              onValueChange={(val) => updateActionCategory(action.id, val)}
                            >
                              <SelectTrigger className="h-7 w-[120px] text-xs">
                                <SelectValue placeholder={t("category")} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">
                                  {t("no_category") || "No Category"}
                                </SelectItem>
                                {categories.map((cat) => (
                                  <SelectItem key={cat._id} value={cat._id}>
                                    <div className="flex items-center gap-1.5">
                                      <div
                                        className="w-2 h-2 rounded-full"
                                        style={{ backgroundColor: cat.color }}
                                      />
                                      {cat.name}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Badge
                              variant="outline"
                              className={cn("text-xs", getPriorityColor(action.priority))}
                            >
                              {action.priority}
                            </Badge>
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => startEditing(index)}
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                            onClick={() => removeAction(action.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Custom Action */}
              <div className="pt-2 border-t">
                <Label className="text-xs text-muted-foreground mb-2 block">
                  {t("add_custom_action") || "Add your own action"}
                </Label>
                <div className="flex gap-2">
                  <Textarea
                    value={customAction}
                    onChange={(e) => setCustomAction(e.target.value)}
                    placeholder={t("enter_action") || "Enter an action item..."}
                    className="min-h-[40px] text-sm resize-none"
                    rows={1}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addCustomAction}
                    disabled={!customAction.trim()}
                    className="h-10"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* No actions generated */}
          {!isGenerating && suggestedActions.length === 0 && (
            <div className="text-center py-8">
              <CheckSquare className="h-12 w-12 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {t("no_actions_generated") ||
                  "No actions could be generated. Add your own below."}
              </p>
              <div className="mt-4 flex gap-2 max-w-sm mx-auto">
                <Textarea
                  value={customAction}
                  onChange={(e) => setCustomAction(e.target.value)}
                  placeholder={t("enter_action") || "Enter an action item..."}
                  className="text-sm"
                />
                <Button
                  variant="outline"
                  onClick={addCustomAction}
                  disabled={!customAction.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0 border-t pt-4">
          <Button variant="outline" onClick={handleClose}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleCreateActions}
            disabled={selectedCount === 0 || isCreating}
            className="bg-primary hover:bg-primary"
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("creating") || "Creating..."}
              </>
            ) : (
              <>
                <CheckSquare className="h-4 w-4 mr-2" />
                {t("create_actions") || "Create Actions"} ({selectedCount})
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InsightToActionDialog;
